import { Collection, Logger, LogManager, MixinInterface } from "@mcleod/core";
import { Component } from "..";
import { ListenerListDef } from "../base/ListenerListDef";

export class BusyMixin extends Component implements MixinInterface {
    private static log: Logger;
    private _busy: boolean;
    private busyPromises: Set<Promise<any>>;
    private _busyWhenDataSourceBusy: boolean;
    
    get name(): string {
        return "Busy";
    }

    initialize() {
    }

    get listenerDefs(): Collection<ListenerListDef> {
        return null;
    }

    public override isBusyComponent(): boolean {
        return true;
    }

    public get busy(): boolean {
        // Last-resort false return value is for compound components that extend Panel, but don't handle all PanelProps
        return this._busy ?? this.getPropertyDefinitions()?.busy?.defaultValue ?? false;
    }

    public set busy(value: boolean) {
        if (this.busy === value) {
            return;
        }
        this._busy = value;
        this.applyBusy();
    }

    protected applyBusy() {
        // Classes using BusyMixin will override this method to define how the component reacts when its busy state
        // changes.
        throw new Error("Component needs to define behavior around when its busy state changes");
    }

    get busyWhenDataSourceBusy(): boolean {
        return this._busyWhenDataSourceBusy ?? this.getPropertyDefinitions()?.busyWhenDataSourceBusy?.defaultValue ?? false;
    }

    set busyWhenDataSourceBusy(value: boolean) {
        this._busyWhenDataSourceBusy = value;
    }

    private addBusyPromise(promise: Promise<any>) {
        this.getLog().debug("Adding busy promise: %o", promise);
        if (this.busyPromises == null) {
            this.busyPromises = new Set<Promise<any>>();
        }
        this.busyPromises.add(promise);
    }

    private removeBusyPromise(promise: Promise<any>) {
        this.getLog().debug("Removing busy promise: %o", promise);
        this.busyPromises?.delete(promise);
        if (this.busyPromisesCount === 0) {
            this.busyPromises = null;
        }
    }

    private get busyPromisesCount(): number {
        return this.busyPromises?.size ?? 0;
    }

    public async makeBusyWhile<T>(promise: Promise<T>): Promise<T> {
        this.addBusyPromise(promise);
        if (!this.busy) {
            this.busy = true;
        }
        try {
            // Wait for this specific promise to complete
            return await promise;
        } finally {
            this.removeBusyPromise(promise);
            if (this.busyPromisesCount === 0) {
                this.busy = false;
            }
        }
    }

    protected getLog() {
        if (BusyMixin.log == null) {
            BusyMixin.log = LogManager.getLogger("components.mixins.BusyMixin");
        }
        return BusyMixin.log;
    }
}
